import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiClient from '../api/api';
import { Map, MapMarker, MapControls } from '../components/ui/map';
import { FaMapMarkerAlt, FaCheckCircle, FaBriefcase, FaCalendarAlt, FaUsers, FaTags } from 'react-icons/fa';

const RadioButton = ({ id, name, value, checked, onChange, label }) => (
  <div className="flex items-center cursor-pointer p-3 border rounded-xl hover:bg-slate-50 transition-colors w-full">
    <input
      id={id}
      name={name}
      type="radio"
      value={value}
      checked={checked}
      onChange={onChange}
      className="focus:ring-emerald-500 h-4 w-4 text-emerald-600 border-gray-300 accent-emerald-600"
    />
    <label htmlFor={id} className="ml-3 block text-sm font-medium text-gray-700 w-full cursor-pointer">
      {label}
    </label>
  </div>
);

function PublishJob() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(jobId);

  const [step, setStep] = useState(1);

  // Form state
  const [jobTitle, setJobTitle] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  
  // Category State
  const [industries, setIndustries] = useState([]); // Parents
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [services, setServices] = useState([]); // Subs
  const [selectedCategory, setSelectedCategory] = useState('');

  // Location State (Unified)
  const [locationText, setLocationText] = useState('');
  const [mapCenter, setMapCenter] = useState([90.4125, 23.8103]);
  const [markerPos, setMarkerPos] = useState({ lng: 90.4125, lat: 23.8103 });

  // Job Type State
  const [jobType, setJobType] = useState('direct'); // 'direct' or 'bidding'

  // Direct Hire Specifics
  const [paymentType, setPaymentType] = useState('contract'); 
  const [rate, setRate] = useState('');
  const [requiredWorkers, setRequiredWorkers] = useState(1); // Default to 1
  const [numberOfHours, setNumberOfHours] = useState('');
  const [numberOfDays, setNumberOfDays] = useState('');
  const [workStartDate, setWorkStartDate] = useState('');
  const [estimatedCompletionDate, setEstimatedCompletionDate] = useState('');
  const [applicationDeadline, setApplicationDeadline] = useState('');

  // Bidding Specifics
  const [biddingStartDate, setBiddingStartDate] = useState('');
  const [biddingStartTimeOnly, setBiddingStartTimeOnly] = useState('');
  const [biddingEndDate, setBiddingEndDate] = useState('');
  const [biddingEndTimeOnly, setBiddingEndTimeOnly] = useState('');
  const [biddingType, setBiddingType] = useState('all');

  // Market Tips State
  const [marketTip, setMarketTip] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Fetch Market Tip when category/location changes
  useEffect(() => {
    const fetchMarketTip = async () => {
        if (!selectedCategory || !selectedCategory.trim()) {
            setMarketTip(null);
            return;
        }
        
        try {
            // Robust District Extraction
            const parts = (locationText || '').split(',').map(p => p.trim());
            let district = 'National';

            const distPart = parts.find(p => p.toLowerCase().includes('district'));
            if (distPart) {
                district = distPart.replace(/ district/i, '');
            } else if (parts.length >= 3) {
                district = parts[parts.length - 3];
            }

            // CLEAN AND EXPLICIT URL
            const url = `/categories/market-rate?category=${encodeURIComponent(selectedCategory.trim())}&district=${encodeURIComponent(district)}`;
            const res = await apiClient.get(url);
            setMarketTip(res.data);
        } catch (err) {
            setMarketTip(null); 
        }
    };

    const timer = setTimeout(fetchMarketTip, 800);
    return () => clearTimeout(timer);
  }, [selectedCategory, locationText]);

  // Fetch Sub-services when industry changes
  useEffect(() => {
    const fetchSubServices = async () => {
        if (!selectedIndustry) {
            setServices([]);
            return;
        }
        try {
            const res = await apiClient.get(`/categories/${selectedIndustry}/sub`);
            setServices(res.data || []);
        } catch (err) {
            console.error("Failed to load sub-services");
        }
    };
    fetchSubServices();
  }, [selectedIndustry]);

  useEffect(() => {
    const fetchInitialData = async () => {
        try {
            // Fetch Parent Categories (Industries)
            const catRes = await apiClient.get('/categories?parentsOnly=true');
            setIndustries(catRes.data || []);

            if (isEditMode) {
                const response = await apiClient.get(`/jobs/${jobId}`);
                const job = response.data;
                setJobTitle(job.title || '');
                setJobDescription(job.description || '');

                if (job.location) {
                    if (typeof job.location === 'object' && job.location.lat) {
                        setMarkerPos({ lat: job.location.lat, lng: job.location.lng });
                        setMapCenter([job.location.lng, job.location.lat]);
                        setLocationText(job.location.address || '');
                    } else if (typeof job.location === 'string') {
                        setLocationText(job.location);
                    }
                }

                setJobType(job.post_model === 'direct_apply' ? 'direct' : 'bidding');
                
                const details = job.details?.[0] || {};
                setRate(details.budget_per_worker || '');
                setRequiredWorkers(details.required_workers || 1);
                
                // For editing, we might need to find the parent of the category
                // This would require a more complex fetch or backend support.
                // Assuming for now it's a new job.
                setSelectedCategory(details.worker_type || ''); 

                if (job.post_model === 'direct_apply') {
                    setPaymentType(job.job_type === 'fixed_contract' ? 'contract' : job.job_type);
                    const directHire = job.direct_hires?.[0] || {};

                    setWorkStartDate(directHire.work_start_date ? new Date(directHire.work_start_date).toISOString().split('T')[0] : '');
                    setEstimatedCompletionDate(directHire.estimated_completion_date ? new Date(directHire.estimated_completion_date).toISOString().split('T')[0] : '');
                    setApplicationDeadline(directHire.Application_Deadline ? new Date(directHire.Application_Deadline).toISOString().split('T')[0] : '');
                } else {
                    const session = details.bidding_sessions?.[0] || {};

                    if (session.start_time) {
                    const start = new Date(session.start_time);
                    setBiddingStartDate(start.toISOString().split('T')[0]);
                    setBiddingStartTimeOnly(start.toTimeString().slice(0,5));
                    }
                    if (session.end_time) {
                    const end = new Date(session.end_time);
                    setBiddingEndDate(end.toISOString().split('T')[0]);
                    setBiddingEndTimeOnly(end.toTimeString().slice(0,5));
                    }
                    setBiddingType(session.bidding_type || 'all');
                }
            }
        } catch (err) {
            console.error(err);
            setError('Failed to load data.');
        } finally {
            setLoading(false);
        }
    };
    fetchInitialData();
  }, [jobId, isEditMode]);

  // Map Logic
  const handleMapClick = async (coords) => {
      setMarkerPos(coords);
      try {
          const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${coords.lat}&lon=${coords.lng}`);
          const data = await response.json();
          if (data && data.display_name) {
              setLocationText(data.display_name);
          }
      } catch (err) {
          console.error("Reverse geocoding failed", err);
      }
  };

  const handleGeolocate = (coords) => {
      setMapCenter([coords.lng, coords.lat]);
      handleMapClick(coords);
  };

  const nextStep = () => {
    if (step === 1) {
      if (!jobTitle.trim() || !jobDescription.trim() || !locationText || !selectedCategory) {
        setError('Please fill title, description, category and location.');
        return;
      }
    } else if (step === 2) {
      // Validate payment/dates
      if (!rate || !requiredWorkers || requiredWorkers < 1) {
          setError('Please specify a valid budget and number of workers.');
          return;
      }

      if (jobType === 'direct') {
          if (!workStartDate || !estimatedCompletionDate) {
              setError('Please fill required dates.');
              return;
          }
      } else {
          if (!biddingStartDate || !biddingEndDate) {
              setError('Please fill bidding schedule.');
              return;
          }
      }
    }
    setError('');
    setStep(prev => prev + 1);
  };

  const prevStep = () => {
    setError('');
    setStep(prev => prev - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    const locationData = {
      address: locationText,
      lat: markerPos.lat,
      lng: markerPos.lng
    };

    let payload = {
      title: jobTitle,
      description: jobDescription,
      location: locationData,
      post_model: jobType === 'direct' ? 'direct_apply' : 'bidding',
      job_type: jobType === 'direct' ? (paymentType === 'contract' ? 'fixed_contract' : paymentType) : 'fixed_contract',
      worker_type: selectedCategory, // Use selected category
      required_workers: parseInt(requiredWorkers),
      budget_per_worker: parseFloat(rate)
    };

    if (jobType === 'direct') {
      if (paymentType === 'hourly' && numberOfHours) payload.estimated_duration_hours = parseInt(numberOfHours);
      else if (paymentType === 'daily' && numberOfDays) payload.estimated_duration_hours = parseInt(numberOfDays) * 8;

      payload.workStartDate = workStartDate;
      payload.estimatedCompletionDate = estimatedCompletionDate;
      payload.applicationDeadline = applicationDeadline;
    } else {
      payload.biddingStartTime = `${biddingStartDate}T${biddingStartTimeOnly}`;
      payload.biddingEndTime = `${biddingEndDate}T${biddingEndTimeOnly}`;
      payload.biddingType = biddingType;
    }

    try {
      if (isEditMode) {
        await apiClient.put(`/jobs/${jobId}`, payload);
        setSuccess('Job updated successfully!');
        setTimeout(() => navigate('/owner-dashboard'), 2000);
      } else {
        await apiClient.post('/jobs', payload);
        setSuccess('Job published successfully!');
        setTimeout(() => navigate('/owner-dashboard'), 2000);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to publish job.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="p-10 text-center text-slate-500">Loading...</div>;

  return (
    <div className="min-h-screen bg-slate-50 p-6 lg:p-10 font-sans">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-3xl font-black text-slate-800 mb-8 tracking-tight">{isEditMode ? 'Edit Job' : 'Create New Job'}</h2>
        
        {/* Progress Bar */}
        <div className="flex items-center mb-10 bg-white p-4 rounded-2xl shadow-sm border border-slate-100">
            {[
                { n: 1, label: "Details" }, 
                { n: 2, label: "Budget & Workers" }, 
                { n: 3, label: "Review" }
            ].map((s, idx) => (
                <React.Fragment key={s.n}>
                    <div className={`flex items-center ${step >= s.n ? 'text-emerald-600' : 'text-slate-300'}`}>
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${step >= s.n ? 'bg-emerald-100' : 'bg-slate-100'}`}>{s.n}</div>
                        <span className={`ml-2 font-bold hidden sm:inline ${step >= s.n ? 'text-slate-800' : 'text-slate-400'}`}>{s.label}</span>
                    </div>
                    {idx < 2 && <div className={`flex-1 h-1 mx-4 rounded-full ${step > s.n ? 'bg-emerald-500' : 'bg-slate-100'}`}></div>}
                </React.Fragment>
            ))}
        </div>

        <div className="bg-white p-8 rounded-[2rem] shadow-lg border border-slate-100">
            
            {step === 1 && (
                <div className="space-y-6 animate-fade-in">
                    <div>
                        <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Job Title</label>
                        <input value={jobTitle} onChange={e => setJobTitle(e.target.value)} className="w-full text-lg font-bold border-b-2 border-slate-200 focus:border-emerald-500 outline-none py-2 transition-colors placeholder:text-slate-300 text-slate-800" placeholder="e.g. Harvest Helpers Needed" />
                    </div>
                    
                    <div>
                        <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Description</label>
                        <textarea value={jobDescription} onChange={e => setJobDescription(e.target.value)} className="w-full border border-slate-200 rounded-xl p-4 text-sm focus:ring-2 ring-emerald-500 outline-none transition-all placeholder:text-slate-300 text-slate-700 min-h-[120px]" placeholder="Describe the work requirements..." />
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">Location</label>
                        <div className="h-80 rounded-2xl overflow-hidden border-4 border-slate-100 shadow-md relative">
                            <Map 
                                center={mapCenter} 
                                zoom={7} 
                                className="h-full w-full"
                                onClick={handleMapClick}
                            >
                                <MapControls showZoom showLocate onGeolocate={handleGeolocate} />
                                <MapMarker 
                                    longitude={markerPos.lng} 
                                    latitude={markerPos.lat} 
                                    draggable 
                                    onDragEnd={handleMapClick} 
                                />
                            </Map>
                            <div className="absolute top-4 left-4 right-14 bg-white/90 backdrop-blur p-3 rounded-xl shadow-sm border border-slate-200 z-10">
                                <p className="text-[10px] font-bold text-emerald-600 uppercase">Selected</p>
                                <p className="text-sm font-bold text-slate-800 line-clamp-1">{locationText || "Pin location on map"}</p>
                            </div>
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Service Industry</label>
                        <select 
                            value={selectedIndustry} 
                            onChange={(e) => {
                                setSelectedIndustry(e.target.value);
                                setSelectedCategory(''); // Reset service
                            }}
                            className="w-full border border-slate-200 rounded-xl p-3 text-sm font-bold text-slate-700 focus:ring-2 ring-emerald-500 outline-none bg-slate-50 mb-4"
                        >
                            <option value="">Select Industry</option>
                            {industries.map(cat => (
                                <option key={cat.id} value={cat.id}>{cat.Name}</option>
                            ))}
                        </select>

                        {selectedIndustry && (
                            <>
                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Specific Service</label>
                                <select 
                                    value={selectedCategory} 
                                    onChange={(e) => setSelectedCategory(e.target.value)}
                                    className="w-full border border-slate-200 rounded-xl p-3 text-sm font-bold text-slate-700 focus:ring-2 ring-emerald-500 outline-none bg-slate-50"
                                >
                                    <option value="">Select Service</option>
                                    {services.map(s => (
                                        <option key={s.id} value={s.Name}>{s.Name}</option>
                                    ))}
                                </select>
                            </>
                        )}
                        
                        {marketTip && (
                            <div className="mt-3 p-4 bg-emerald-50 rounded-xl border border-emerald-100 flex items-start gap-3 animate-fade-in">
                                <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center text-emerald-600 shadow-sm flex-shrink-0">
                                    <FaTags size={14} />
                                </div>
                                <div>
                                    <p className="text-[10px] font-black text-emerald-600 uppercase tracking-widest">Market Insight</p>
                                    <p className="text-xs font-bold text-slate-700">
                                        Average rate for {selectedCategory} in {marketTip.district} is <span className="text-emerald-700 font-black">৳{marketTip.average_rate}/{marketTip.unit}</span>.
                                    </p>
                                    <p className="text-[10px] text-slate-400 mt-0.5">Recommended range: ৳{marketTip.min_rate} - ৳{marketTip.max_rate}</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {step === 2 && (
                <div className="space-y-8 animate-fade-in">
                    <div>
                        <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Hiring Model</label>
                        <div className="grid grid-cols-2 gap-4">
                            <RadioButton id="direct" name="jobType" value="direct" checked={jobType === 'direct'} onChange={(e) => setJobType(e.target.value)} label="Direct Hire" />
                            <RadioButton id="bidding" name="jobType" value="bidding" checked={jobType === 'bidding'} onChange={(e) => setJobType(e.target.value)} label="Bidding" />
                        </div>
                    </div>

                    <div className="p-6 bg-slate-50 rounded-2xl border border-slate-100">
                        <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Resources</label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                            <div>
                                <label className="text-sm font-bold text-slate-700 block mb-1">Budget Per Worker (BDT)</label>
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold">৳</span>
                                    <input type="number" value={rate} onChange={e => setRate(e.target.value)} className="w-full pl-8 pr-4 py-2 rounded-lg border border-slate-200 focus:border-emerald-500 outline-none font-bold text-slate-800" placeholder="0.00" />
                                </div>
                            </div>

                            <div>
                                <label className="text-sm font-bold text-slate-700 block mb-1">Required Workers</label>
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"><FaUsers /></span>
                                    <input type="number" min="1" value={requiredWorkers} onChange={e => setRequiredWorkers(e.target.value)} className="w-full pl-9 pr-4 py-2 rounded-lg border border-slate-200 focus:border-emerald-500 outline-none font-bold text-slate-800" placeholder="1" />
                                </div>
                            </div>
                            
                            {jobType === 'direct' ? (
                                <>
                                    <div>
                                        <label className="text-sm font-bold text-slate-700 block mb-1">Start Date</label>
                                        <input type="date" value={workStartDate} onChange={e => setWorkStartDate(e.target.value)} className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:border-emerald-500 outline-none text-sm" />
                                    </div>
                                    <div>
                                        <label className="text-sm font-bold text-slate-700 block mb-1">End Date</label>
                                        <input type="date" value={estimatedCompletionDate} onChange={e => setEstimatedCompletionDate(e.target.value)} className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:border-emerald-500 outline-none text-sm" />
                                    </div>
                                    <div>
                                        <label className="text-sm font-bold text-slate-700 block mb-1">Application Deadline</label>
                                        <input type="date" value={applicationDeadline} onChange={e => setApplicationDeadline(e.target.value)} className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:border-emerald-500 outline-none text-sm" />
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div>
                                        <label className="text-sm font-bold text-slate-700 block mb-1">Bid Start</label>
                                        <div className="flex gap-2">
                                            <input type="date" value={biddingStartDate} onChange={e => setBiddingStartDate(e.target.value)} className="flex-1 px-2 py-2 rounded-lg border text-sm" />
                                            <input type="time" value={biddingStartTimeOnly} onChange={e => setBiddingStartTimeOnly(e.target.value)} className="w-24 px-2 py-2 rounded-lg border text-sm" />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="text-sm font-bold text-slate-700 block mb-1">Bid End</label>
                                        <div className="flex gap-2">
                                            <input type="date" value={biddingEndDate} onChange={e => setBiddingEndDate(e.target.value)} className="flex-1 px-2 py-2 rounded-lg border text-sm" />
                                            <input type="time" value={biddingEndTimeOnly} onChange={e => setBiddingEndTimeOnly(e.target.value)} className="w-24 px-2 py-2 rounded-lg border text-sm" />
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {step === 3 && (
                <div className="animate-fade-in space-y-6 text-slate-600">
                    <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
                        <FaBriefcase className="text-emerald-500 mt-1" />
                        <div>
                            <p className="text-xs font-bold text-slate-400 uppercase">Job</p>
                            <h4 className="text-lg font-black text-slate-800">{jobTitle}</h4>
                            <p className="text-sm font-bold text-emerald-600">{selectedCategory}</p>
                            <p className="text-sm mt-1">{jobDescription}</p>
                        </div>
                    </div>
                    
                    <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
                        <FaMapMarkerAlt className="text-emerald-500 mt-1" />
                        <div>
                            <p className="text-xs font-bold text-slate-400 uppercase">Location</p>
                            <p className="font-bold text-slate-800">{locationText}</p>
                        </div>
                    </div>

                    <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
                        <FaCalendarAlt className="text-emerald-500 mt-1" />
                        <div>
                            <p className="text-xs font-bold text-slate-400 uppercase">Schedule & Pay</p>
                            <p className="font-bold text-slate-800">৳{rate} <span className="text-slate-400 font-normal">({jobType === 'direct' ? 'Fixed' : 'Starting Bid'})</span></p>
                            <p className="font-bold text-slate-800 mt-1">{requiredWorkers} <span className="text-slate-400 font-normal">Workers Needed</span></p>
                            <p className="text-sm mt-1">
                                {jobType === 'direct' 
                                    ? `Starts: ${workStartDate}` 
                                    : `Bidding ends: ${biddingEndDate}`}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {error && <div className="mt-6 p-4 bg-red-50 text-red-700 rounded-xl font-bold text-center text-sm">{error}</div>}
            {success && <div className="mt-6 p-4 bg-green-50 text-green-700 rounded-xl font-bold text-center text-sm flex items-center justify-center gap-2"><FaCheckCircle/> {success}</div>}

            <div className="mt-10 pt-6 border-t border-slate-100 flex justify-between">
                {step > 1 && (
                    <button onClick={prevStep} className="px-6 py-3 rounded-xl font-bold text-slate-500 hover:bg-slate-50 transition-colors">
                        Back
                    </button>
                )}
                {step < 3 ? (
                    <button onClick={nextStep} className="ml-auto bg-slate-900 text-white px-8 py-3 rounded-xl font-bold hover:bg-slate-800 transition-all shadow-lg hover:shadow-xl">
                        Continue
                    </button>
                ) : (
                    <button onClick={handleSubmit} disabled={loading} className="ml-auto bg-emerald-600 text-white px-10 py-3 rounded-xl font-bold hover:bg-emerald-700 transition-all shadow-lg shadow-emerald-200 hover:shadow-xl disabled:opacity-50 flex items-center">
                        {loading && <div className="animate-spin w-4 h-4 border-2 border-white/30 border-t-white rounded-full mr-2"></div>}
                        Publish Now
                    </button>
                )}
            </div>
        </div>
      </div>
    </div>
  );
}

export default PublishJob;
